import React, { Children, useMemo, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';

import { Image } from '../image/Image';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  actions?: React.Node,
  children?: React.Node,
  className?: string,
  disclaimers?: React.Node,
  externalImageSrc?: string,
  hasAnimation?: Boolean,
  imageUUID?: string,
  mode?: string,
  productSummary: React.Node,
  servings?: string,
  subtitle?: React.Node,
};

export const CheckoutCard = ({
  actions,
  children,
  className,
  disclaimers,
  externalImageSrc,
  hasAnimation,
  imageUUID,
  mode,
  productSummary,
  servings,
  subtitle,
}: Props) => {
  const [hasImage, toggleImage] = useState(true);
  const componentTheme = useComponentTheme(theme => theme.components.CheckoutCard, mode);
  const theme = useMemo(() => ({ componentTheme }), [componentTheme]);
  const onImageError = useCallback(() => toggleImage(v => !v), [toggleImage]);
  const [checkoutCardContent, setCheckoutCardContent] = useState();

  return (
    <ThemeProvider theme={theme}>
      {/* TODO: divider should sit outside of this component... */}
      <Separator aria-hidden="true" />

      <Container className={className} data-test="bundle-container" hasAnimation={hasAnimation}>
        {hasImage && (
          <ImageContainer
            alt=""
            mode="square"
            uuid={imageUUID}
            externalSrc={externalImageSrc}
            sizes="33vw"
            onError={onImageError}
          />
        )}

        <p className="hide-text-show-focus" data-test="checkout-card-hidden-text">
          {checkoutCardContent}
        </p>

        <Details
          aria-hidden="true"
          data-test="checkout-card-details"
          ref={checkoutCardRef => setCheckoutCardContent(checkoutCardRef?.innerText.replace(/\n+/giu, ', '))}
        >
          <Header>
            {productSummary}
            {servings && componentTheme.Servings.visible && <Servings>{servings}</Servings>}
            {subtitle && componentTheme.Subtitle.visible && <Subtitle>{subtitle}</Subtitle>}
            {disclaimers}
          </Header>

          {!!Children.toArray(children).length && <Content>{children}</Content>}
        </Details>

        {actions && <Actions>{actions}</Actions>}
      </Container>
    </ThemeProvider>
  );
};

const Separator = styled.hr`
  grid-area: separator;
  margin: 16px 0;
  width: 100%;
  border: 0;
  border-bottom: ${({ theme }) => theme.stroke.light};

  &:first-of-type {
    display: none;
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    margin: 24px 0;
  }
`;

const Container = styled.article`
  display: grid;
  position: relative;
  grid-template-areas: 'separator separator' 'image header' 'image content' 'actions actions';
  grid-template-columns: auto 1fr;
  grid-template-rows: auto;
  column-gap: 16px;
  width: 100%;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    grid-template-areas: 'separator separator' 'image header' 'image content' 'image actions';
    column-gap: 24px;
  }

  @-webkit-keyframes show {
    to {
      max-height: 100%;
      opacity: 1;
    }
  }

  @keyframes show {
    to {
      max-height: 100%;
      opacity: 1;
    }
  }

  ${({ hasAnimation }) =>
    hasAnimation &&
    `
      overflow: hidden;
      opacity: 1;
      max-height: 100%;
      animation: hide 0.25s ease-in-out forwards;
      -webkit-animation: hide 0.25s ease-in-out forwards;
    `}

  ${({ hasAnimation }) =>
    !hasAnimation &&
    `
      opacity: 0;
      max-height: 0;
      animation: show 0.25s ease-in-out forwards;
      -webkit-animation: show 0.25s ease-in-out forwards;
    `}

  @-webkit-keyframes hide {
    to {
      opacity: 0;
      max-height: 0;
    }
  }

  @keyframes hide {
    to {
      opacity: 0;
      max-height: 0;
    }
  }
`;

const Details = styled.div`
  display: contents;
`;

const ImageContainer = styled(Image)`
  grid-area: image;
  display: inline-block;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: 16px;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    display: block;
    width: 92px;
    height: 92px;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    margin-bottom: unset;
  }
`;

const Header = styled.header`
  grid-area: header;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  min-width: 0;
`;

const Content = styled.div`
  grid-area: content;
  margin-bottom: 16px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  grid-area: actions;
  gap: 12px;
  align-items: center;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    gap: 24px;
  }
`;

const Subtitle = styled.span`
  font-size: ${({ theme: { componentTheme } }) => componentTheme.Subtitle.fontSize};
  color: ${({ theme: { componentTheme } }) => componentTheme.Subtitle.color};
  grid-area: subtitle;
  margin-top: 4px;
`;

const Servings = styled.span`
  font-size: ${({ theme: { componentTheme } }) => componentTheme.Servings.fontSize};
  color: ${({ theme: { componentTheme } }) => componentTheme.Servings.color};
  grid-area: servings;
  margin-top: 4px;
`;
